<template>
  <div class="space-y-6">
    <!-- Back Button -->
    <button
      @click="$emit('back')"
      class="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
    >
      <svg
        class="w-6 h-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      <span class="text-xl font-medium text-gray-700">Back</span>
    </button>

    <!-- Classes Card -->
    <div class="bg-white rounded-xl shadow-md p-6">
      <!-- Header -->
      <div class="border-b border-gray-200 pb-4 mb-8">
        <h2 class="text-2xl font-semibold text-gray-900">Classes</h2>
      </div>

      <!-- Date Navigation -->
      <div class="flex items-center justify-center gap-4 mb-8">
        <button
          @click="previousDay"
          class="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 transition"
        >
          <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
        <span
          class="text-base font-semibold text-gray-700 min-w-32 text-center"
        >
          {{ formattedDate }}
        </span>
        <button
          @click="nextDay"
          class="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-900 transition"
        >
          <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
          </svg>
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <svg
          class="animate-spin h-8 w-8 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>

      <!-- Classes List -->
      <div v-else class="space-y-4">
        <!-- Table Header -->
        <div class="hidden md:flex items-center justify-between">
          <span
            class="text-base font-semibold text-gray-500 uppercase tracking-wide"
          >
            Class
          </span>
          <span
            class="text-base font-semibold text-gray-500 uppercase tracking-wide"
          >
            Check-ins
          </span>
        </div>

        <!-- No Events -->
        <div v-if="events.length === 0" class="py-8 text-center">
          <p class="text-lg text-gray-500">
            No classes scheduled for this day.
          </p>
        </div>

        <!-- Mobile Event Cards -->
        <div v-if="events.length > 0" class="md:hidden space-y-3">
          <div
            v-for="event in sortedEvents"
            :key="'mobile-' + event.id"
            class="border border-gray-200 rounded-lg p-4"
          >
            <!-- Row 1: Event Name -->
            <div class="text-base font-semibold text-gray-900">
              {{ event.name }}
            </div>
            <!-- Row 2: Time, Check-ins, Attendees. nowrap on both because at
                 phone widths "0 checked in" otherwise breaks across three lines. -->
            <div class="mt-2 flex items-center justify-between gap-3">
              <div class="flex items-center gap-4 text-sm text-gray-600 whitespace-nowrap">
                <span>{{ formatTime(event) }}</span>
                <span>{{ getAttendanceCount(event) }} checked in</span>
              </div>
              <button
                @click="viewAttendees(event)"
                class="text-sm font-semibold underline transition hover:opacity-70 whitespace-nowrap flex-shrink-0"
                :style="{ color: teamPrimaryColor }"
              >
                Attendees
              </button>
            </div>
            <!-- Row 3: expected trials, on its own line so it never competes
                 with the check-in count for horizontal room -->
            <div
              v-if="expectedTrialCount(event) > 0"
              class="mt-1 text-sm font-medium text-yellow-600"
            >
              {{ expectedTrialLabel(event) }}
            </div>
          </div>
        </div>

        <!-- Desktop Event Rows -->
        <div v-if="events.length > 0" class="hidden md:block">
          <div
            v-for="event in sortedEvents"
            :key="'desktop-' + event.id"
            class="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-6 py-3 border-b border-gray-100 last:border-0"
          >
            <!-- Time -->
            <span
              class="text-xl font-medium text-gray-700 whitespace-nowrap min-w-[5rem]"
            >
              {{ formatTime(event) }}
            </span>
            <!-- Event Name -->
            <span class="text-xl font-medium text-gray-700">
              {{ event.name }}
            </span>
            <!-- Attendance -->
            <span
              class="text-xl font-medium text-gray-700 whitespace-nowrap text-right"
            >
              {{ getAttendanceCount(event) }} checked in
              <span
                v-if="expectedTrialCount(event) > 0"
                class="block text-base font-medium text-yellow-600"
              >
                {{ expectedTrialLabel(event) }}
              </span>
            </span>
            <!-- Attendees Button -->
            <button
              @click="viewAttendees(event)"
              class="text-base font-semibold underline transition hover:opacity-70 whitespace-nowrap"
              :style="{ color: teamPrimaryColor }"
            >
              Attendees
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KioskClassesView',
  props: {
    store: {
      type: Object,
      required: true,
    },
  },
  emits: ['back', 'view-attendees'],
  data() {
    return {
      selectedDate: new Date(),
      events: [],
      loading: false,
      // Cache for attendance counts
      attendanceCounts: {},
      // Booked-but-not-yet-checked-in trials per event, filled by the same
      // pass that loads attendance counts
      expectedTrialCounts: {},
      // Request counter for race condition prevention
      requestId: 0,
      // Track whether attendance counts are still loading
      attendanceLoading: true,
    };
  },
  computed: {
    formattedDate() {
      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      return this.selectedDate
        .toLocaleDateString('en-US', options)
        .toUpperCase()
        .replace(',', '');
    },
    sortedEvents() {
      return [...this.events].sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    },
    teamPrimaryColor() {
      return this.store?.state.teamInfo?.brand_color || '#622733';
    },
  },
  mounted() {
    this.loadEventsForDate();
  },
  methods: {
    previousDay() {
      const newDate = new Date(this.selectedDate);
      newDate.setDate(newDate.getDate() - 1);
      this.selectedDate = newDate;
      this.loadEventsForDate();
    },
    nextDay() {
      const newDate = new Date(this.selectedDate);
      newDate.setDate(newDate.getDate() + 1);
      this.selectedDate = newDate;
      this.loadEventsForDate();
    },
    async loadEventsForDate() {
      // Increment and capture request ID to prevent race conditions
      const currentRequestId = ++this.requestId;

      this.loading = true;
      this.attendanceLoading = true;
      this.events = [];
      this.attendanceCounts = {};
      this.expectedTrialCounts = {};

      try {
        const start = moment(this.selectedDate).startOf('day').toISOString();
        const end = moment(this.selectedDate).endOf('day').toISOString();
        const params = `?start=${start}&end=${end}&preventCache=${new Date().getTime()}`;
        const url = this.$apiService.eventsUrl(params);

        const events = await this.$apiService.loadAllPages(url, 'events');

        // Check if this request is still current before applying results
        if (currentRequestId !== this.requestId) return;

        this.events = events;

        // Load attendance counts for all events
        await this.loadAttendanceCounts(currentRequestId);
      } catch (error) {
        // Check if this request is still current before showing error
        if (currentRequestId !== this.requestId) return;

        console.error('Failed to load events:', error);
        this.$notificationManager.emit(
          'show-toast',
          'Failed to load classes',
          false,
        );
      } finally {
        // Only update loading state if this request is still current
        if (currentRequestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    async loadAttendanceCounts(currentRequestId) {
      // Load attendance count for each event
      const promises = this.events.map(async (event) => {
        try {
          const url = this.$apiService.eventCheckInsUrl(event.id, 'all');
          // Both lists are needed together: "expected" means booked with no
          // check-in, so the pending count can only be worked out from the pair.
          const [checkIns, expectedTrials] = await Promise.all([
            this.$apiService.loadAllPages(url, 'check_ins'),
            this.loadExpectedTrials(event),
          ]);

          // Check if this request is still current before applying results
          if (currentRequestId !== this.requestId) return;

          const checkedIn = new Set(checkIns.map((c) => c.wrestler_profile_id));

          this.attendanceCounts = {
            ...this.attendanceCounts,
            [event.id]: checkIns.length,
          };
          this.expectedTrialCounts = {
            ...this.expectedTrialCounts,
            [event.id]: expectedTrials.filter((t) => !checkedIn.has(t.wrestler_profile_id)).length,
          };
        } catch (error) {
          // Check if this request is still current before applying error state
          if (currentRequestId !== this.requestId) return;

          console.error(
            `Failed to load attendance for event ${event.id}:`,
            error,
          );
          this.attendanceCounts[event.id] = 0;
          this.expectedTrialCounts[event.id] = 0;
        }
      });

      await Promise.all(promises);

      // Only update attendanceLoading state if this request is still current
      if (currentRequestId === this.requestId) {
        this.attendanceLoading = false;
      }
    },
    // Resolves to [] rather than throwing so a failure here can't blank out the
    // check-in count sitting next to it.
    async loadExpectedTrials(event) {
      try {
        const url = this.$apiService.eventExpectedTrialsUrl(event.id);
        const response = await axios.get(url);
        return (response.data && response.data.expected_trials) || [];
      } catch (error) {
        console.error(`Failed to load expected trials for event ${event.id}:`, error);
        return [];
      }
    },
    expectedTrialCount(event) {
      if (this.attendanceLoading) {
        return 0;
      }
      return this.expectedTrialCounts[event.id] || 0;
    },
    expectedTrialLabel(event) {
      const count = this.expectedTrialCount(event);
      return `${count} trial${count === 1 ? '' : 's'} expected`;
    },
    getAttendanceCount(event) {
      // Show loading indicator while attendance counts are being fetched
      if (this.attendanceLoading) {
        return '...';
      }
      const count = this.attendanceCounts[event.id] || 0;
      // If event has max_attendees, show as count/max, otherwise just count
      if (event.max_attendees) {
        return `${count}/${event.max_attendees}`;
      }
      return count.toString();
    },
    formatTime(event) {
      if (!event) return '';

      // Handle all-day events
      if (event.all_day) {
        return 'All day';
      }

      if (!event.start_at) return '';
      const date = new Date(event.start_at);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
    viewAttendees(event) {
      // Emit event to parent with event and selected date
      this.$emit('view-attendees', { event, selectedDate: this.selectedDate });
    },
  },
};
</script>
